import { motion } from 'framer-motion';
import { SparklesIcon, CpuChipIcon, UserGroupIcon, ChartBarIcon, CheckCircleIcon, ArrowRightIcon, DocumentChartBarIcon } from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';

interface FloatingOrbProps {
  className: string;
  delay?: number;
}

const FloatingOrb = ({ className, delay = 0 }: FloatingOrbProps) => (
  <motion.div
    initial={{ opacity: 0, scale: 0 }}
    animate={{ 
      opacity: 1, 
      scale: 1,
      y: [0, -20, 0],
      x: [0, 40, 0],
    }}
    transition={{
      duration: 8,
      repeat: Infinity,
      repeatType: "reverse",
      ease: "easeInOut",
      opacity: { duration: 1, delay }
    }}
    className={`absolute rounded-full blur-3xl ${className}`}
  />
);

const FeatureCard = ({ icon: Icon, title, link }: { icon: any, title: string, link: string }) => (
  <Link to={link}>
    <motion.div
      whileHover={{ scale: 1.02, y: -4 }}
      className="group relative flex flex-col items-center p-6 rounded-xl bg-white/80 backdrop-blur-sm border border-primary/10 shadow-sm hover:shadow-lg hover:border-[#FF7066]/30 transition-all duration-300 cursor-pointer"
    >
      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-[#FF7066]/5 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
      <div className="relative z-10 flex flex-col items-center">
        <div className="mb-4 p-3 rounded-lg bg-primary/5 group-hover:bg-[#FF7066]/10 transition-colors duration-300">
          <Icon className="w-6 h-6 text-secondary group-hover:text-[#FF7066] transition-colors duration-300" />
        </div>
        <span className="text-primary font-medium text-lg">{title}</span>
      </div>
    </motion.div>
  </Link>
);

const HeroSection = () => {
  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-white via-primary/5 to-[#FF7066]/5">
        <motion.div
          className="absolute inset-0 bg-grid-pattern opacity-[0.02]"
          animate={{
            backgroundPosition: ['0% 0%', '100% 100%'],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            repeatType: "reverse",
          }}
        />
      </div>

      {/* Floating Orbs */}
      <FloatingOrb className="w-96 h-96 bg-primary/20 -top-48 -left-48" delay={0} />
      <FloatingOrb className="w-96 h-96 bg-[#FF7066]/20 top-1/2 -right-48" delay={0.2} />
      <FloatingOrb className="w-96 h-96 bg-accent/20 bottom-0 left-1/2" delay={0.4} />
      
      {/* Content */}
      <div className="container relative z-10 text-center px-8 sm:px-6 lg:px-8 mt-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="flex justify-center mb-6 mt-6"
        >
          <span className="inline-flex items-center px-4 py-2 rounded-full bg-white/80 backdrop-blur-sm text-[#FF7066] text-sm font-medium border border-[#FF7066]/20">
            <SparklesIcon className="w-4 h-4 mr-2" />
            Innovative AI for Modern Enterprises
          </span>
        </motion.div>

        <motion.h1 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-4xl md:text-6xl font-bold mb-6 mt-6 text-primary"
        >
          Revolutionise Your Business with 
          <span className="text-[#FF7066]"> Bespoke AI</span>
        </motion.h1>

        <motion.p 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="text-xl text-secondary mb-8 max-w-2xl mx-auto"
        >
          We craft intelligent solutions that deliver measurable ROI through AI agents, knowledge systems, and advanced analytics tailored to your unique business challenges.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-20"
        >
          <a
            href="https://calendly.com/amrit-betterbyai/30min"
            target="_blank"
            rel="noopener noreferrer"
            className="px-6 py-3 bg-[#FF7066] hover:bg-[#FF8A82] text-white font-medium rounded-md shadow-sm hover:shadow-md transition-all"
          >
            Book a Meeting
          </a>
          <Link
            to="/services"
            className="px-6 py-3 bg-transparent border border-secondary hover:border-[#FF7066] text-secondary hover:text-[#FF7066] font-medium rounded-md transition-colors"
          >
            Learn More
          </Link>
        </motion.div>

        {/* Feature Highlights */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto mb-16"
        >
          <FeatureCard 
            icon={CpuChipIcon} 
            title="Custom AI Solutions" 
            link="/services#custom-ai-solutions"
          />
          <FeatureCard 
            icon={UserGroupIcon} 
            title="AI Agents" 
            link="/services#ai-appointment-setters"
          />
          <FeatureCard 
            icon={SparklesIcon} 
            title="Machine Learning" 
            link="/services#data-science-solutions"
          />
        </motion.div>
        
        {/* New AI Audit Promo Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.8 }}
          className="max-w-5xl mx-auto mb-24 relative"
        >
          <div className="absolute inset-0 bg-gradient-to-br from-[#FF7066]/10 to-accent/10 rounded-2xl transform rotate-1"></div>
          <div className="relative bg-white/90 backdrop-blur-sm rounded-2xl border border-[#FF7066]/20 p-8 shadow-lg overflow-hidden">
            {/* Decorative elements */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-[#FF7066]/10 to-accent/5 rounded-full -mr-20 -mt-20 blur-2xl"></div>
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-gradient-to-tr from-primary/10 to-[#FF7066]/5 rounded-full -ml-20 -mb-20 blur-2xl"></div>
            
            <div className="flex flex-col md:flex-row items-center justify-between gap-8 relative z-10">
              <div className="text-left">
                <div className="inline-flex items-center px-4 py-1.5 rounded-full bg-[#FF7066]/10 text-[#FF7066] text-sm font-medium mb-4">
                  <DocumentChartBarIcon className="w-4 h-4 mr-2" />
                  Complimentary AI Business Analysis
                </div>
                <h2 className="text-3xl font-bold text-primary mb-4">
                  Uncover Your <span className="text-[#FF7066]">Untapped AI Potential</span>
                </h2>
                <p className="text-secondary text-lg mb-6">
                  Receive a bespoke assessment revealing precisely how intelligent automation can boost your efficiency and profitability.
                </p>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
                  {[
                    "Pinpoint inefficiencies ripe for automation",
                    "Uncover hidden revenue opportunities",
                    "Outpace competitors with AI innovation",
                    "Prioritised implementation strategy"
                  ].map((benefit, idx) => (
                    <div key={idx} className="flex items-center">
                      <CheckCircleIcon className="w-5 h-5 text-[#FF7066] mr-2 flex-shrink-0" />
                      <span className="text-sm text-secondary">{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="flex-shrink-0 w-full md:w-auto">
                <Link
                  to="/free-ai-audit"
                  className="w-full md:w-auto inline-flex items-center justify-center px-6 py-4 bg-[#FF7066] hover:bg-[#FF8A82] text-white font-medium rounded-lg shadow-md hover:shadow-lg transition-all group"
                >
                  Claim Your Free AI Audit
                  <ArrowRightIcon className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </Link>
                <p className="text-xs text-gray-500 mt-2 text-center md:text-right">
                  Just 2 minutes to complete • Zero obligation
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8, delay: 1 }}
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
      >
        <div className="w-6 h-10 border-2 border-[#FF7066]/30 rounded-full p-1">
          <motion.div
            animate={{
              y: [0, 12, 0],
            }}
            transition={{
              duration: 1.5,
              repeat: Infinity,
              repeatType: "loop",
            }}
            className="w-1.5 h-1.5 bg-[#FF7066] rounded-full mx-auto"
          />
        </div>
      </motion.div>
    </section>
  );
};

export default HeroSection;
